import React from "react";
import "./ImpactStories.css";

/* ================= SVG ICONS ================= */
const PlayIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M8 5v14l11-7z"/>
  </svg>
);

const PhoneIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ff5900" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const QuoteWatermarkIcon = () => (
  <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="rgba(255, 89, 0, 0.1)" strokeWidth="1">
    <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z" />
    <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

/* ================= COMPONENT ================= */
function ImpactStories() {
  
  const galleryItems = [
    { img: "../images/rr1.jpg", title: "Rural Education Drive", category: "Outreach" },
    { img: "../images/rro2.jpg", title: "Free Medical Diagnostics", category: "Healthcare" },
    { img: "../images/rr3.jpg", title: "Women Empowerment Workshops", category: "Livelihood" },
    { img: "../images/rr4.jpg", title: "Anniversary Celebration", category: "Relief" }
  ];

  const testimonials = [
    {
      quote: "The vocational guidance and sewing tools provided by the trust completely turned my family's financial situation around. I am now independent.",
      name: "Lakshmi Devi",
      role: "Beneficiary, Puducherry",
      avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=200&q=80"
    },
    
    {
      quote: "Absolute operational transparency. Every rupee contributed translates directly to verifiable grassroots essentials. Proud to support them.",
      name: "Ramesh Narayan",
      role: "Corporate Donor",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&q=80"
    }
  ];

  const blogPosts = [
    {
      date: "14 JAN 2024",
      category: "Education",
      title: "Importance of education in rural areas",
      desc: "Analyzing the systemic transformation guaranteed when remote villages receive accessible tutoring, digital literacy facilities, and consistent mentorship.",
      img: "../images/blog1.jpg"
    },
    {
      date: "28 JAN 2024",
      category: "Healthcare",
      title: "Health awareness campaigns",
      desc: "Documenting our preventative outreach methodologies combating malnutrition, distributing clinical essentials, and establishing localized hygiene routines.",
      img: "../images/blog2.jpg"
    },
    {
      date: "10 FEB 2024",
      category: "Empowerment",
      title: "Women empowerment success stories",
      desc: "Spotlighting remarkable regional artisans transitioning from absolute marginalization to running independent cooperative entrepreneurial networks.",
      img: "../images/blog3.jpg"
    }
  ];

  return (
    <div className="layout-suite-wrapper">
      
      {/* ================= HERO BANNER (FIXED TO FULL SCREEN PROPORTIONS) ================= */}
      <section className="suite-hero-fullscreen">
        
        <div className="hero-content-box">
          <span className="hero-subline">SOLUTIONS FOR OUR COMMUNITY</span>
          <h1 className="hero-main-title">Innovative Charity<br />Solutions</h1>
          <div className="hero-cta-row">
            <a href="/DonateNow" className="btn-vibrant-orange">Discover More</a>
            <button className="btn-watch-trailer">
              <span className="play-circle"><PlayIcon /></span>
              <span>Watch Video</span>
            </button>
          </div>
        </div>
      </section>

      {/* ================= SECTION 1: OVERLAPPING EXPERTISE ================= */}
      <section className="section-expertise">
        <div className="suite-container">
          <div className="grid-expertise">
            
            <div className="expertise-text-block">
              <span className="badge-meta">WELCOME TO TRUST</span>
              <h2 className="section-heavy-title">Expert of Grassroots<br />Empowerment</h2>
              <div className="decorative-dots-grid"></div>
              
              <p className="lead-paragraph">
                Anuk Charitable Trust implements deep structural methodologies guaranteeing that marginalized communities are not just temporarily aided, but sustainably equipped.
              </p>
              
              <div className="feature-highlight-box">
                <div className="icon-badge-box">🤝</div>
                <p>Establishing verifiable avenues of lifelong opportunity through localized relief initiatives and direct educational sponsorships.</p>
              </div>

              <div className="action-row-split">
                <a href="/AboutUs" className="btn-dark-solid">Discover more</a>
                <div className="quick-call-block">
                  <div className="phone-icon-circle"><PhoneIcon /></div>
                  <div className="phone-labels">
                    <span className="label-top">Call Us Anytime</span>
                    <span className="label-num">+91 86681 06574</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="expertise-visual-block">
              <div className="visual-composition-frame">
                <img 
                  src="../images/about1.jpg" 
                  alt="Community Aid" 
                  className="img-primary-back" 
                />
                <img 
                  src="../images/about2.jpg" 
                  alt="Child Welfare" 
                  className="img-secondary-front" 
                />
                
                <div className="badge-experience-square">
                  <span className="square-num">7+</span>
                  <span className="square-text">Years<br />Experience</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ================= SECTION 2: HIGH CONTRAST ORANGE RIBBON ================= */}
      <section className="section-orange-ribbon">
        <div className="suite-container ribbon-flex">
          <h2 className="ribbon-heading">We're Ready to Develop Our Community!</h2>
          <a href="/Programs" className="btn-black-pill">More Services</a>
        </div>
      </section>

      {/* ================= SECTION 3: STRATEGIC PREVIEW GRID ================= */}
      <section className="section-programs-preview">
        <div className="suite-container">
          <div className="header-centered">
            <span className="badge-meta">MEET OUR INITIATIVES</span>
            <h2 className="section-heavy-title center">Ready to give a new<br />life foundation</h2>
          </div>

          <div className="programs-dual-grid">
            
            <div className="program-preview-card">
              <div className="card-thumb-wrap">
                <img src="../images/ed1.jpg" alt="Education" />
                <span className="card-letter-tag">E</span>
              </div>
              <div className="card-body-wrap">
                <h3>Education &amp; Mentorship</h3>
                <p>Providing direct scholarships, digital infrastructure, and foundational supplies to deserving rural youth.</p>
                <a href="/" className="btn-oval-black">Discover More</a>
              </div>
            </div>

            <div className="program-preview-card">
              <div className="card-thumb-wrap">
                <img src="../images/ed2.jpg" alt="Healthcare" />
                <span className="card-letter-tag">H</span>
              </div>
              <div className="card-body-wrap">
                <h3>Healthcare Outreach</h3>
                <p>Deploying specialized preventive care camps, diagnostic units, and direct nutritional distribution systems.</p>
                <a href="/" className="btn-oval-black">Discover More</a>
              </div>
            </div>

            <div className="program-preview-card">
              <div className="card-thumb-wrap">
                <img src="../images/ed3.jpg" alt="Healthcare" />
                <span className="card-letter-tag">H</span>
              </div>
              <div className="card-body-wrap">
                <h3>Sustainable Development Programs</h3>
                <p>Driving long-term impact through community-focused initiatives that empower individuals with skills, opportunities, and sustainable resources for a brighter future.</p>
                <a href="/" className="btn-oval-black">Discover More</a>
              </div>
            </div>

            <div className="program-preview-card">
              <div className="card-thumb-wrap">
                <img src="../images/ed4.jpg" alt="Healthcare" />
                <span className="card-letter-tag">H</span>
              </div>
              <div className="card-body-wrap">
                <h3>Women Empowerment Initiatives</h3>
                <p>Supporting women through vocational training, entrepreneurship guidance, and self-help programs that encourage financial independence and social confidence.</p>
                <a href="/" className="btn-oval-black">Discover More</a>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ================= SECTION 4: DARK GALLERY BLOCK ================= */}
      <section className="section-dark-gallery">
        
        <div className="gallery-top-band">
          <div className="suite-container band-flex">
            <div>
              <span className="badge-meta-light">RECENTLY CAPTURED MOMENTS</span>
              <h2 className="band-heavy-title">Moments of Impact</h2>
              <p className="band-desc">Browse through images and videos showcasing our work, events, and community outreach programs. This registry builds absolute operational trust and authenticity.</p>
            </div>
            <a href="/Programs" className="btn-vibrant-orange">View all work</a>
          </div>
        </div>

        <div className="suite-container">
          <div className="gallery-overlap-grid">
            {galleryItems.map((item, idx) => (
              <div key={idx} className="gallery-item-frame">
                <img src={item.img} alt={item.title} loading="lazy" />
                <div className="gallery-item-caption">
                  <span className="cat-tag">{item.category}</span>
                  <h4>{item.title}</h4>
                </div>
              </div>
            ))}
          </div>
        </div>

      </section>

      

      {/* ================= SECTION 6: SPLIT SEO / TRUST BLOCK ================= */}
      <section className="section-split-seo">
        <div className="suite-container">
          <div className="grid-seo">
            
            <div className="seo-visual-box">
              <img 
                src="../images/age1.jpg" 
                alt="Community Trust Operations" 
                className="img-seo-main" 
              />
            </div>

            <div className="seo-text-box">
              <h2 className="section-heavy-title">Consistent Trust<br />Optimized Expert</h2>
              <p className="seo-intro">
                We operate under structured organizational mandates guaranteeing maximum accountability. Posting regular impact logs optimizes public visibility while fostering systemic participation.
              </p>

              <ul className="seo-check-list">
                <li>
                  <CheckCircleIcon />
                  <span>Direct grassroots empowerment without middle-tier attrition.</span>
                </li>
                <li>
                  <CheckCircleIcon />
                  <span>Transparent quarterly balance registries and public audit files.</span>
                </li>
                <li>
                  <CheckCircleIcon />
                  <span>Inclusive engagement connecting rural artisans directly to local markets.</span>
                </li>
              </ul>

              <a href="/AboutUs" className="btn-vibrant-orange mt-4">Learn More</a>
            </div>

          </div>
        </div>
      </section>

     
     

    </div>
  );
}

export default ImpactStories;